import { useQuery } from "@tanstack/react-query";
import { Users } from "lucide-react";
import { DialogQueryState } from "@/components/shared/DialogQueryState";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiGet } from "@/lib/api-client";
import { policyKeys } from "@/lib/query";
import { serviceTeamsDataSchema } from "@/schemas/policy-dialogs";

interface ServiceTeamDialogProps {
  policyNumber: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function RoleField({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="min-w-0">
      <div className="text-muted-foreground text-[11px] font-medium">{label}</div>
      <div className="mt-0.5 break-words font-medium text-sm" title={value ?? undefined}>
        {value ?? "—"}
      </div>
    </div>
  );
}

export function ServiceTeamDialog({ policyNumber, open, onOpenChange }: ServiceTeamDialogProps) {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: policyKeys.serviceTeam(policyNumber),
    queryFn: () =>
      apiGet(
        `/api/policies/${encodeURIComponent(policyNumber)}/service-team`,
        serviceTeamsDataSchema,
      ),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Service Team</DialogTitle>
          <DialogDescription>Policy {policyNumber}</DialogDescription>
        </DialogHeader>

        <DialogQueryState
          isLoading={isLoading}
          isError={isError}
          error={error}
          onRetry={() => refetch()}
          errorMessage="Failed to load service team"
          isEmpty={
            !isLoading &&
            !isError &&
            data !== undefined &&
            data.underwriting === null &&
            data.claims.length === 0
          }
          emptyIcon={Users}
          emptyMessage="No service team data on file."
        />

        {data && (data.underwriting !== null || data.claims.length > 0) && (
          <div className="max-h-[65vh] space-y-6 overflow-y-auto">
            <section>
              <h3 className="mb-3 font-medium text-primary text-sm">Underwriting Service Team</h3>
              {data.underwriting ? (
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-md border bg-card p-4 sm:grid-cols-3">
                  <RoleField label="Agency No." value={data.underwriting.agentNumber} />
                  <RoleField label="Underwriter" value={data.underwriting.underwriterName} />
                  <RoleField label="Account Analyst" value={data.underwriting.accountAnalystName} />
                  <RoleField
                    label="Policy Issuance Coordinator"
                    value={data.underwriting.policyIssuanceCoordinatorName}
                  />
                  <RoleField label="Billing Analyst" value={data.underwriting.billingAnalystName} />
                  <RoleField
                    label="Loss Control Consultant"
                    value={data.underwriting.lossControlConsultantName}
                  />
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No underwriting team on file.</p>
              )}
            </section>

            <section>
              <h3 className="mb-3 font-medium text-primary text-sm">Claim Service Team</h3>
              {data.claims.length > 0 ? (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full table-fixed text-sm">
                    <thead className="bg-muted/30">
                      <tr className="border-b text-left text-muted-foreground text-xs">
                        <th className="w-[14%] px-3 py-2 font-medium">Region</th>
                        <th className="w-[20%] px-3 py-2 font-medium">Jurisdiction State</th>
                        <th className="w-[20%] px-3 py-2 font-medium">UW Company</th>
                        <th className="w-[23%] px-3 py-2 font-medium">Adjuster</th>
                        <th className="w-[23%] px-3 py-2 font-medium">Manager</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.claims.map((m) => (
                        <tr key={m.id} className="border-b last:border-0">
                          <td className="break-words px-3 py-2" title={m.region ?? undefined}>
                            {m.region ?? "—"}
                          </td>
                          <td
                            className="break-words px-3 py-2"
                            title={m.jurisdictionState ?? undefined}
                          >
                            {m.jurisdictionState ?? "—"}
                          </td>
                          <td className="break-words px-3 py-2" title={m.uwCompany ?? undefined}>
                            {m.uwCompany ?? "—"}
                          </td>
                          <td
                            className="break-words px-3 py-2"
                            title={m.claimAdjusterName ?? undefined}
                          >
                            {m.claimAdjusterName ?? "—"}
                          </td>
                          <td
                            className="break-words px-3 py-2"
                            title={m.claimTeamManagerName ?? undefined}
                          >
                            {m.claimTeamManagerName ?? "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No claim team assignments.</p>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
